import { defineStore } from 'pinia';
import { type Component, isRef, markRaw, type Ref, type ComputedRef } from 'vue';

type DynStr = string | Ref<string> | ComputedRef<string> | (() => string);

export type HeaderAction = {
	icon?: () => string;
	btnClass?: () => string;
	title?: () => string;
	onClick?: () => void;
	id?: string;
	component?: Component;
};

export type HeaderActionHandle = {
	remove: () => void;
	update?: (
		next: Partial<{ icon: DynStr; btnClass: DynStr; title: DynStr; onClick: () => void; id: string }>,
	) => void;
};

function toGetter(v: DynStr): () => string {
	if (typeof v === 'function') return () => (v as () => string)();
	if (isRef(v)) return () => v.value;
	return () => v;
}

type ActionOptions = {
	icon?: DynStr;
	btnClass?: DynStr;
	title?: DynStr;
	onClick?: () => void;
	id?: string;
	component?: Component;
};

/** Adds an action to one of the navbar action lists and hands back a handle to it. */
function addAction(list: HeaderAction[], opts: ActionOptions): HeaderActionHandle {
	const { icon, btnClass, title, onClick, id, component } = opts;
	// If an action with same onClick or id exists, reuse that one and still return a handle for it
	let existing = list.find((a) => (onClick && a.onClick === onClick) || (id && a.id === id));
	if (!existing) {
		existing = {
			icon: icon ? toGetter(icon) : undefined,
			btnClass: btnClass ? toGetter(btnClass) : undefined,
			title: title ? toGetter(title) : undefined,
			onClick,
			id,
			component: component ? markRaw(component) : undefined,
		};
		list.push(existing);
	}

	const remove = () => {
		const idx = list.indexOf(existing!);
		if (idx !== -1) list.splice(idx, 1);
	};

	const update = (
		next: Partial<{ icon: DynStr; btnClass: DynStr; title: DynStr; onClick: () => void; id: string }>,
	) => {
		if (next.icon !== undefined) existing!.icon = toGetter(next.icon);
		if (next.btnClass !== undefined) existing!.btnClass = toGetter(next.btnClass);
		if (next.title !== undefined) existing!.title = toGetter(next.title);
		if (next.onClick !== undefined) existing!.onClick = next.onClick;
		if (next.id !== undefined) existing!.id = next.id;
	};

	return { remove, update };
}

/** localStorage key the language the user picked is kept under. */
export const APP_LOCALE_STORAGE_KEY = 'app.locale';

/** The language the user picked on an earlier visit, null when they never picked one. */
function loadAppLocale(): string | null {
	try {
		return globalThis.localStorage?.getItem(APP_LOCALE_STORAGE_KEY) || null;
	} catch {
		return null;
	}
}

export const useAppOptionsStore = defineStore({
	id: 'appOption',
	state: () => ({
		title: globalThis.config?.['app.name'] || globalThis.env?.app?.name || 'EF Vue SPA App',
		title_short: globalThis.config?.['app.short_name'],
		backgroundUrlExtern: globalThis.config?.['app.background_url_extern'] ?? '/img/login-background.jpg',

		is_minimized: false,

		appTheme: 'light' as 'light' | 'dark' | 'auto',
		appThemeDividers: false,
		appThemeGradients: true,
		appThemeShadows: false,

		/** The language the user picked, kept across reloads; null follows the browser, see plugins/i18n. */
		appLocale: loadAppLocale(),

		appPrimaryColor: globalThis.config?.['app.primary_color'] ?? '#0d6efd',
		appSecondaryColor: globalThis.config?.['app.secondary_color'] ?? '#6c757d',

		/** The app's own palette for its colour fields, null for the lib's (colorPalette()). */
		colorPresets: null as string[] | null,

		showApplications: globalThis.config?.['app.show_applications'] ?? '1',

		appSidebarCollapsed: false,
		appSidebarMinified: false,
		appSidebarSearch: false,
		appFooter: true,

		is_locked: false,

		// Set while a context switch is in flight, see the context plugin: the main layout puts a
		// spinner where the router view is, so no page renders or loads against the context that
		// is on its way out.
		is_context_switching: false,

		search: undefined as
			| undefined
			| {
					callback?: (s: string) => boolean;
					component?: Component;
					displayBreakpoint?: string;
			  },

		headerActions: [] as HeaderAction[],

		// Actions shown next to the context switchers, for app wide selections that scope the
		// data of every page, e.g. a year picker.
		contextActions: [] as HeaderAction[],

		routerKey: Date.now().toString(),

		// We allow the override of Layout Components, e.g. the Context Menu to visualise App specific data
		contextHeaderSlot: null as null | Component,
	}),
	actions: {
		refreshRouterView() {
			this.routerKey = Date.now().toString();
		},
		/** Picks the language for good, over the browser's. */
		setAppLocale(locale: string) {
			this.appLocale = locale;
			try {
				globalThis.localStorage?.setItem(APP_LOCALE_STORAGE_KEY, locale);
			} catch {
				// Storage blocked: the pick holds until the next reload.
			}
		},
		/**
		 * Takes a public app setting just saved in the settings, so the page shows it without a
		 * reload. The state the layout reads from it follows; any other key waits for one.
		 */
		applyConfig(key: string, value: string | null) {
			if (globalThis.config) globalThis.config[key] = value ?? '';

			switch (key) {
				case 'app.name':
					this.title = value || globalThis.env?.app?.name || 'EF Vue SPA App';
					break;
				case 'app.short_name':
					this.title_short = value ?? '';
					break;
				case 'app.primary_color':
					this.appPrimaryColor = value || '#0d6efd';
					break;
				case 'app.secondary_color':
					this.appSecondaryColor = value || '#6c757d';
					break;
				case 'app.show_applications':
					this.showApplications = value ?? '1';
					break;
			}
		},
		toggleIsMinimized() {
			this.is_minimized = !this.is_minimized;
		},
		addHeaderAction(opts: ActionOptions): HeaderActionHandle {
			return addAction(this.headerActions, opts);
		},
		addContextAction(opts: ActionOptions): HeaderActionHandle {
			return addAction(this.contextActions, opts);
		},
		clearHeaderActions() {
			this.headerActions = [] as HeaderAction[];
		},
		clearContextActions() {
			this.contextActions = [] as HeaderAction[];
		},
		/*init() {
			this.title = window.config?.['app.name'] || 'FetSy 2.0';
			this.title_short = window.config?.['app.name.short'] || this.title;
			this.loginBackgroundUrl = window.config?.['app.login.background'] || '/img/login-background.jpg';
			this.registerBackgroundUrl = window.config?.['app.register.background'] || '/img/login-background.jpg';
		},*/
	},
});

export function hexToRgbList(hex: string) {
	const h = hex.replace('#', '');
	const [r, g, b] =
		h.length === 3 ?
			[...h].map((c) => parseInt(c + c, 16))
		:	[h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)].map((c) => parseInt(c, 16));
	return `${r}, ${g}, ${b}`;
}

/**
 * The text colour that reads better on a fill of the given colour, white or the dark ink, by
 * their contrast against it. The primary is set at runtime by an admin, so a light one must not
 * leave its buttons unreadable.
 */
export function readableOn(hex: string): string {
	const channel = (value: number) => {
		const c = value / 255;

		return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	};
	const [r, g, b] = hexToRgbList(hex).split(', ').map(Number);
	const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

	// No colour to judge - unset or malformed - keeps Bootstrap's white.
	if (Number.isNaN(luminance)) return '#fff';

	// #212529 has a luminance of 0.0181. A mid tone can fail AA with both; it gets the better one.
	const onWhite = 1.05 / (luminance + 0.05);
	const onDark = (luminance + 0.05) / (0.0181 + 0.05);

	return onWhite >= onDark ? '#fff' : '#212529';
}

/** Bootstrap's own colours, its $colors map. */
const BOOTSTRAP_COLORS = [
	'#0d6efd', // blue
	'#6610f2', // indigo
	'#6f42c1', // purple
	'#d63384', // pink
	'#dc3545', // red
	'#fd7e14', // orange
	'#ffc107', // yellow
	'#198754', // green
	'#20c997', // teal
	'#0dcaf0', // cyan
	'#6c757d', // gray
	'#343a40', // gray-dark
	'#000000', // black
	'#ffffff', // white
];

/** The palette of Laravel's old front-end preset, which the first apps took their colours from. */
const PRESET_COLORS = ['#38c172', '#e3342f', '#ffed4a', '#adb5bd', '#6cb2eb', '#3490dc', '#6c757d', '#ffffff'];

/** A colour mixed with white, Bootstrap's tint-color(): a weight of 0.6 is its 200 shade. */
export function tint(hex: string, weight = 0.6): string {
	const channels = hexToRgbList(hex).split(', ').map(Number);

	return '#' + channels.map((c) => Math.round(c + (255 - c) * weight).toString(16).padStart(2, '0')).join('');
}

/**
 * What a colour field (efColorInput) offers beside its picker, each colour followed by its light
 * version: the app's primary, Bootstrap's colours and those of Laravel's old preset, each once.
 * EfLibOptions.colorPresets replaces it.
 */
export function colorPalette(primary: string): string[] {
	const colors = [primary, ...BOOTSTRAP_COLORS, ...PRESET_COLORS].map((color) => color.toLowerCase());

	return [...new Set(colors.flatMap((color) => [color, tint(color)]))];
}
